import { useCallback, useEffect, useRef, useTransition } from "react";
import { useFormContext } from "react-hook-form";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  CrudApi,
  FeedbackFormFields,
  Log,
} from "@ryde/shared";

export const useFeedbacksEditDrawerLogic = (): void => {
  const {
    watch,
    setValue,
    formState: { touchedFields, dirtyFields },
  } = useFormContext();

  const [, startTransition] = useTransition();
  const setIsLoading = useSetRecoilState(apiDataFetchingAtom);

  const cacheSolutionsIds = useRef<Record<number, string>>({});

  const feedbackSolutionId = watch(FeedbackFormFields.feedbackSolutionId);

  const isTouched = !!touchedFields[FeedbackFormFields.feedbackSolutionId];
  const isDirty = !!dirtyFields[FeedbackFormFields.feedbackSolutionId];
  const feedbackSolutionIdChanged = isDirty || isTouched;

  const fetchSolutionAndSetViewOnChange = useCallback(async () => {
    if (!feedbackSolutionId) {
      Log.debug("Feedback solution ID is empty, skipping fetch");
      setValue(FeedbackFormFields.adminRemarks, "", {
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    // use cache if available
    const cached = cacheSolutionsIds.current[feedbackSolutionId];

    if (cached !== undefined) {
      Log.debug("Using cached solution for ID:", feedbackSolutionId);
      setValue(FeedbackFormFields.adminRemarks, cached, {
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const solution = await CrudApi.getById(
        "feedbackSolution",
        feedbackSolutionId,
      );

      const remarks = solution?.metadata?.remarks ?? "";
      setValue(FeedbackFormFields.adminRemarks, remarks, {
        shouldDirty: true,
        shouldTouch: true,
      });
      cacheSolutionsIds.current[feedbackSolutionId] = remarks;
    } catch (error) {
      Log.error("Error fetching feedback solution", error);
    } finally {
      setIsLoading(false);
    }
  }, [feedbackSolutionId, setIsLoading, setValue]);

  useEffect(() => {
    if (!feedbackSolutionIdChanged) {
      return;
    }

    startTransition(() => {
      fetchSolutionAndSetViewOnChange();
    });
  }, [feedbackSolutionIdChanged, fetchSolutionAndSetViewOnChange]);
};
